import { Info, Plus } from "lucide-react";

import { ButtonV2 } from "../../../components/ui/button/v2";
import { SaveButtonV2 } from "../../../components/ui/button/v2/SaveButton";
import NoDataIcon from "../../../assets/svg/EmptyTableIcon";
import { PhysicalTechnicalVerificationTable } from "./PhysicalTechnicalVerificationTable";
import { LegalVerificationTable } from "./LegalVerificationTable";

interface VerificationContainerProps {
  loanReferenceId: string;
}

export interface SaveFooterProps {
  isSaving: boolean;
  disabled: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

export const SaveFooter: React.FC<SaveFooterProps> = ({
  isSaving,
  disabled,
  onSave,
  onDiscard,
}) => (
  <div className="bg-slate-100 flex flex-row-reverse gap-2 items-center p-2 rounded-md mt-4">
    <SaveButtonV2
      size="sm"
      buttonText="Save"
      loading={isSaving}
      disabled={disabled || isSaving}
      onClick={onSave}
    />

    <ButtonV2
      variant="ghost"
      size="sm"
      disabled={isSaving}
      onClick={onDiscard}
    >
      Cancel
    </ButtonV2>

    <span className="text-sm font-semibold leading-5 text-[#737373]">
      {isSaving
        ? "Saving changes..."
        : "You have made changes, save now"}
    </span>
  </div>
);

interface VerificationTableSectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

export const VerificationTableSection: React.FC<VerificationTableSectionProps> = ({
  title,
  subtitle,
  children,
}) => (
  <div className="flex flex-col gap-2 w-full">
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <span className="text-[12px] font-medium leading-none text-[#0A0A0A]">
          {title}
        </span>
        {!subtitle && (
          <Info strokeWidth={1.6} className="w-4 h-4 text-[#737373] flex-shrink-0" />
        )}
      </div>

      {subtitle && (
        <div className="flex items-center gap-1">
          <Info strokeWidth={1.6} className="w-4 h-4 text-[#737373] flex-shrink-0" />
          <span className="text-[12px] font-normal leading-none text-[#737373]">
            {subtitle}
          </span>
        </div>
      )}
    </div>

    <div className="w-full overflow-x-auto">
      {children}
    </div>
  </div>
);

interface VerificationEmptyTableBodyProps {
  title: string;
  subtitle?: string;
  onAdd: () => void;
}

const DEFAULT_EMPTY_SUBTITLE =
  "Document verification will be enabled in a later step";

export const VerificationEmptyTableBody: React.FC<VerificationEmptyTableBodyProps> = ({
  title,
  subtitle = DEFAULT_EMPTY_SUBTITLE,
  onAdd,
}) => (
  <div className="flex flex-col items-center justify-center py-8 px-4">
    <NoDataIcon />
    <span className="text-[#0A0A0A] font-sans font-semibold text-base leading-[1.5rem] text-center mt-4 mb-2 max-w-full">
      {title}
    </span>
    <span className="text-[#737373] font-sans font-normal text-sm leading-[1.25rem] text-center mb-4 max-w-md">
      {subtitle}
    </span>
    <ButtonV2 onClick={onAdd}>
      <Plus className="w-4 h-4 mr-1" /> Add Verification
    </ButtonV2>
  </div>
);

const VerificationContainer: React.FC<
  VerificationContainerProps
> = ({ loanReferenceId }) => {

  return (
    <div className="w-full flex flex-col">
      <div className="flex flex-col gap-[36px] w-full">
        <LegalVerificationTable loanReferenceId={loanReferenceId} />

        <PhysicalTechnicalVerificationTable loanReferenceId={loanReferenceId} />
      </div>
    </div>
  );
};

export default VerificationContainer;
